(() => {
  if (window.customElements.get('cinematic-product')) return;

  class CinematicProduct extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.form = this.querySelector('[data-cinematic-form]');
      this.variantInput = this.querySelector('[data-cinematic-variant-id-input]');
      this.qtyInput = this.querySelector('[data-cinematic-qty-input]');
      this.heroImage = this.querySelector('.cinematic-product__watch-image');
      this.priceEls = [...this.querySelectorAll('.pdp__price, #cinematic-hero-price, .sticky-atc__price')];
      this.addToCartButtons = [...this.querySelectorAll('.cinematic-product__atc, .cinematic-product__sticky-button')];
      this.variantButtons = [...this.querySelectorAll('.cinematic-product__variant-button')];
      this.mediaDots = [...this.querySelectorAll('.cinematic-product__media-dot')];
      this.lifestyleGallery = this.querySelector('[data-cinematic-lifestyle]');
      this.lifestyleTrack = this.querySelector('[data-lifestyle-track]');
      this.lifestyleSlides = [...this.querySelectorAll('[data-lifestyle-slide]')];
      this.lifestylePrev = this.querySelector('[data-lifestyle-prev]');
      this.lifestyleNext = this.querySelector('[data-lifestyle-next]');
      this.lifestyleFilterButtons = [...this.querySelectorAll('[data-lifestyle-filter-value]')];
      this.lifestyleFilterValue = this.lifestyleFilterButtons.find((button) => button.classList.contains('selected'))?.dataset.lifestyleFilterValue || '';
      this.variants = this.parseVariants();
      this.selectedOptions = this.collectSelectedOptions();
      this.currentVariant = this.findVariant() || this.variants.find((variant) => variant.id === Number(this.variantInput?.value)) || this.variants[0];

      this.setupReveal();
      this.setupParallax();
      this.setupVariantPicker();
      this.setupQuantity();
      this.setupMediaRail();
      this.setupLifestyleGallery();
      this.setupStickyButton();
      this.setupAccordions();
      this.syncHeroPrice();
      this.updateUI();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.priceObserver?.disconnect();
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
      if (this.frame) cancelAnimationFrame(this.frame);
    }

    parseVariants() {
      const script = this.querySelector('#product-variants-json');
      if (!script) return [];
      try {
        return JSON.parse(script.textContent || '[]');
      } catch (error) {
        return [];
      }
    }

    collectSelectedOptions() {
      const values = {};
      this.querySelectorAll('.cinematic-product__variant-group [data-option].selected').forEach((button) => {
        values[button.dataset.option] = button.dataset.value;
      });
      return values;
    }

    findVariant() {
      if (!this.variants.length) return null;
      return this.variants.find((variant) => {
        return [1, 2, 3].every((index) => {
          const selected = this.selectedOptions[String(index)];
          if (!selected) return true;
          return variant[`option${index}`] === selected;
        });
      }) || null;
    }

    formatPrice(variant) {
      if (!variant) return '';
      const compare = Number(variant.compare_at_price || 0) > Number(variant.price || 0) ? `<s>${variant.compare_at_price_formatted}</s>` : '';
      return `${compare}<span>${variant.price_formatted || ''}</span>`;
    }

    setupReveal() {
      const items = [...this.querySelectorAll('[data-cinematic-reveal]')];
      if (this.reducedMotion || !('IntersectionObserver' in window)) {
        items.forEach((item) => item.classList.add('is-visible'));
        return;
      }
      this.observer = new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      }, { threshold: 0.08, rootMargin: '0px 0px -8% 0px' });
      items.forEach((item) => this.observer.observe(item));
    }

    setupParallax() {
      const target = this.querySelector('[data-cinematic-parallax]');
      const hero = this.querySelector('.cinematic-product__hero');
      if (!target || !hero || this.reducedMotion) return;
      let ticking = false;
      const update = () => {
        ticking = false;
        const rect = hero.getBoundingClientRect();
        const progress = Math.max(0, Math.min(1, -rect.top / Math.max(1, rect.height)));
        target.style.setProperty('--cinematic-parallax-y', `${progress * 42}px`);
      };
      this.onScroll = () => {
        if (ticking) return;
        ticking = true;
        this.frame = requestAnimationFrame(update);
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      update();
    }

    setupVariantPicker() {
      if (!this.variantButtons.length) return;
      this.variantButtons.forEach((button) => {
        button.addEventListener('click', () => {
          if (button.classList.contains('unavailable')) return;
          const option = button.dataset.option;
          const value = button.dataset.value;
          this.selectedOptions[option] = value;
          this.querySelectorAll(`.cinematic-product__variant-button[data-option="${CSS.escape(option)}"]`).forEach((peer) => {
            peer.classList.toggle('selected', peer.dataset.value === value);
          });
          const matchedVariant = this.findVariant();
          if (matchedVariant) {
            this.currentVariant = matchedVariant;
          }
          this.updateUI();
        });
      });
    }

    setupQuantity() {
      if (!this.qtyInput) return;
      this.querySelectorAll('[data-cinematic-qty]').forEach((button) => {
        button.addEventListener('click', () => {
          const current = Math.max(1, parseInt(this.qtyInput.value || '1', 10));
          this.qtyInput.value = button.dataset.cinematicQty === 'increase' ? current + 1 : Math.max(1, current - 1);
        });
      });
      this.qtyInput.addEventListener('change', () => {
        const current = parseInt(this.qtyInput.value || '1', 10);
        this.qtyInput.value = Number.isFinite(current) && current > 0 ? current : 1;
      });
    }

    setHeroImageSource(src) {
      if (!src || !this.heroImage) return;

      // Shopify's image_tag outputs a responsive srcset. If only `src` is
      // changed, the browser may continue rendering the old srcset candidate.
      this.heroImage.removeAttribute('srcset');
      this.heroImage.removeAttribute('sizes');
      this.heroImage.src = src;
    }

    setupMediaRail() {
      if (!this.mediaDots.length || !this.heroImage) return;
      this.mediaDots.forEach((button) => {
        button.addEventListener('click', () => {
          const src = button.dataset.src;
          if (!src) return;

          this.mediaDots.forEach((item) => {
            const isActive = item === button;
            item.classList.toggle('active', isActive);
            item.setAttribute('aria-pressed', String(isActive));
          });

          this.setHeroImageSource(src);
        });
      });
    }

    setupLifestyleGallery() {
      if (!this.lifestyleGallery || !this.lifestyleTrack || !this.lifestyleSlides.length) {
        this.refreshHeroRenderRail();
        return;
      }

      this.buildMediaGroups();
      this.lifestylePrev?.addEventListener('click', () => this.scrollLifestyle(-1));
      this.lifestyleNext?.addEventListener('click', () => this.scrollLifestyle(1));

      this.lifestyleFilterButtons.forEach((button) => {
        button.addEventListener('click', () => {
          this.lifestyleFilterValue = button.dataset.lifestyleFilterValue || '';
          this.lifestyleFilterButtons.forEach((peer) => {
            const active = peer === button;
            peer.classList.toggle('selected', active);
            peer.setAttribute('aria-pressed', String(active));
          });
          this.filterLifestyleGallery();
        });
      });

      this.refreshHeroRenderRail();
      this.filterLifestyleGallery();
    }

    getColorOptionPosition() {
      const fromGallery = Number(this.lifestyleGallery?.dataset.filterOptionPosition || 0);
      if (fromGallery > 0) return fromGallery;
      const groups = [...this.querySelectorAll('.cinematic-product__variant-group')];
      const colorGroup = groups.find((group) => {
        const label = group.querySelector('.cinematic-product__variant-label')?.textContent?.trim().toLowerCase() || '';
        return /colorway|colour|color/.test(label);
      });
      return Number(colorGroup?.querySelector('[data-option]')?.dataset.option || 1);
    }

    normalizeMediaGroup(value) {
      return String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '');
    }

    buildMediaGroups() {
      if (!this.lifestyleSlides.length) return;
      const colorPosition = this.getColorOptionPosition();
      const slidesById = new Map(this.lifestyleSlides.map((slide) => [String(slide.dataset.mediaId || ''), slide]));
      const anchors = [];
      const seen = new Set();

      this.variants.forEach((variant) => {
        const mediaId = String(variant.featured_media_id || '');
        const slide = slidesById.get(mediaId);
        const value = variant[`option${colorPosition}`] || variant.option1 || '';
        const group = this.normalizeMediaGroup(value);
        if (!slide || !group || seen.has(group)) return;
        seen.add(group);
        anchors.push({
          group,
          value,
          index: Number(slide.dataset.mediaIndex || 0),
          mediaId
        });
      });

      anchors.sort((a, b) => a.index - b.index);
      this.mediaGroupAnchors = anchors;
      const positions = new Map();

      this.lifestyleSlides.forEach((slide) => {
        const mediaIndex = Number(slide.dataset.mediaIndex || 0);
        let anchor = null;
        for (const candidate of anchors) {
          if (candidate.index <= mediaIndex) anchor = candidate;
          else break;
        }
        if (!anchor && anchors.length) anchor = anchors[0];
        const group = anchor?.group || '';
        const position = (positions.get(group) || 0) + 1;
        positions.set(group, position);
        slide.dataset.mediaGroup = group;
        slide.dataset.groupPosition = String(position);
      });

      this.mediaDots.forEach((dot) => {
        const mediaIndex = Number(dot.dataset.mediaIndex || 0);
        let anchor = null;
        for (const candidate of anchors) {
          if (candidate.index <= mediaIndex) anchor = candidate;
          else break;
        }
        if (!anchor && anchors.length) anchor = anchors[0];
        const matchingSlide = slidesById.get(String(dot.dataset.mediaId || ''));
        dot.dataset.mediaGroup = anchor?.group || matchingSlide?.dataset.mediaGroup || '';
        dot.dataset.groupPosition = matchingSlide?.dataset.groupPosition || '';
      });

      this.hasExplicitLifestyle = this.lifestyleSlides.some((slide) => slide.dataset.explicitLifestyle === 'true');
    }

    getCurrentPurchaseColor() {
      const position = this.getColorOptionPosition();
      return this.currentVariant?.[`option${position}`] || this.selectedOptions?.[String(position)] || '';
    }

    refreshHeroRenderRail() {
      if (!this.mediaDots.length) return;
      const rendersPerColor = Math.max(1, Number(this.lifestyleGallery?.dataset.rendersPerColor || 3));
      const fallbackLimit = Math.max(1, Number(this.lifestyleGallery?.dataset.fallbackRenderLimit || 6));
      const purchaseGroup = this.normalizeMediaGroup(this.getCurrentPurchaseColor());
      const hasGroups = Array.isArray(this.mediaGroupAnchors) && this.mediaGroupAnchors.length > 0;
      let visible = [];

      this.mediaDots.forEach((dot, index) => {
        const group = dot.dataset.mediaGroup || '';
        const position = Number(dot.dataset.groupPosition || 0);
        let show;
        if (hasGroups && purchaseGroup) {
          show = group === purchaseGroup && position > 0 && position <= rendersPerColor;
        } else {
          show = index < fallbackLimit;
        }
        dot.hidden = !show;
        if (show) visible.push(dot);
      });

      if (!visible.length) {
        visible = this.mediaDots.slice(0, fallbackLimit);
        visible.forEach((dot) => { dot.hidden = false; });
      }

      visible.forEach((dot, index) => {
        const span = dot.querySelector('span');
        if (span) span.textContent = String(index + 1).padStart(2, '0');
      });

      const currentMediaId = String(this.currentVariant?.featured_media_id || '');
      const active = visible.find((dot) => String(dot.dataset.mediaId || '') === currentMediaId) || visible[0];
      this.mediaDots.forEach((dot) => {
        const isActive = dot === active;
        dot.classList.toggle('active', isActive);
        dot.setAttribute('aria-pressed', String(isActive));
      });
    }

    getLifestyleAliases(value) {
      const base = String(value || '').trim().toLowerCase();
      if (!base) return [];
      const compact = base.replace(/[^a-z0-9]/g, '');
      const map = {
        'blue': ['blue', 'bu'], 'bu': ['bu', 'blue'],
        'green': ['green', 'gn', 'gr'], 'gn': ['gn', 'green'], 'gr': ['gr', 'green'],
        'black': ['black', 'bk'], 'bk': ['bk', 'black'],
        'silver': ['silver', 'sl'], 'sl': ['sl', 'silver'],
        'gunmetal': ['gunmetal', 'gun metal', 'gm'], 'gm': ['gm', 'gunmetal', 'gun metal'],
        'rosegold': ['rose gold', 'rose-gold', 'rosegold', 'rg'], 'rg': ['rg', 'rose gold', 'rose-gold', 'rosegold']
      };
      return [...new Set([base, compact, ...(map[compact] || [])].filter(Boolean))];
    }

    lifestyleKeyMatches(key, alias) {
      const haystack = String(key || '').toLowerCase();
      const needle = String(alias || '').toLowerCase();
      if (!needle) return false;
      if (needle.length <= 3) {
        const escaped = needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        return new RegExp(`(^|[^a-z0-9])${escaped}([^a-z0-9]|$)`, 'i').test(haystack);
      }
      return haystack.includes(needle);
    }

    filterLifestyleGallery() {
      if (!this.lifestyleGallery || !this.lifestyleSlides.length) return;
      const rendersPerColor = Math.max(1, Number(this.lifestyleGallery.dataset.rendersPerColor || 3));
      const fallbackLimit = Math.max(1, Number(this.lifestyleGallery.dataset.fallbackRenderLimit || 6));
      const selectedValue = this.lifestyleFilterValue || this.getCurrentPurchaseColor();
      const selectedGroup = this.normalizeMediaGroup(selectedValue);
      const hasGroups = Array.isArray(this.mediaGroupAnchors) && this.mediaGroupAnchors.length > 0;
      const aliases = this.getLifestyleAliases(selectedValue);

      let lifestyleCandidates = this.lifestyleSlides.filter((slide, index) => {
        if (this.hasExplicitLifestyle) return slide.dataset.explicitLifestyle === 'true';
        if (hasGroups) return Number(slide.dataset.groupPosition || 0) > rendersPerColor;
        return index >= fallbackLimit;
      });

      let matching = lifestyleCandidates;
      if (selectedGroup && hasGroups) {
        const grouped = lifestyleCandidates.filter((slide) => slide.dataset.mediaGroup === selectedGroup);
        if (grouped.length) matching = grouped;
      } else if (aliases.length) {
        const tagged = lifestyleCandidates.filter((slide) => aliases.some((alias) => this.lifestyleKeyMatches(slide.dataset.mediaKey || '', alias)));
        if (tagged.length) matching = tagged;
      }

      const matchingSet = new Set(matching);
      this.lifestyleSlides.forEach((slide) => { slide.hidden = !matchingSet.has(slide); });
      this.lifestyleGallery.hidden = matching.length === 0;
      const hideArrows = matching.length <= 1;
      if (this.lifestylePrev) this.lifestylePrev.hidden = hideArrows;
      if (this.lifestyleNext) this.lifestyleNext.hidden = hideArrows;
      this.lifestyleTrack.scrollTo({ left: 0, behavior: this.reducedMotion ? 'auto' : 'smooth' });
    }

    scrollLifestyle(direction) {
      if (!this.lifestyleTrack) return;
      const firstVisible = this.lifestyleSlides.find((slide) => !slide.hidden);
      const gap = parseFloat(getComputedStyle(this.lifestyleTrack).gap || '12') || 12;
      const distance = firstVisible ? firstVisible.getBoundingClientRect().width + gap : this.lifestyleTrack.clientWidth * 0.75;
      this.lifestyleTrack.scrollBy({ left: distance * direction, behavior: this.reducedMotion ? 'auto' : 'smooth' });
    }

    setupStickyButton() {
      const stickyButton = this.querySelector('.cinematic-product__sticky-button');
      if (!stickyButton || !this.form) return;
      stickyButton.addEventListener('click', () => {
        const submitter = this.querySelector('.cinematic-product__atc');
        if (submitter && typeof this.form.requestSubmit === 'function') {
          this.form.requestSubmit(submitter);
        } else {
          this.form.submit();
        }
      });
    }

    setupAccordions() {
      this.querySelectorAll('[data-cinematic-accordion-button]').forEach((button) => {
        const item = button.closest('.cinematic-product__accordion');
        const panel = item?.querySelector('[data-cinematic-accordion-panel]');
        if (!panel) return;
        if (button.getAttribute('aria-expanded') === 'true') {
          item?.classList.add('open');
          panel.hidden = false;
        }
        button.addEventListener('click', () => {
          const isOpen = button.getAttribute('aria-expanded') === 'true';
          button.setAttribute('aria-expanded', String(!isOpen));
          item?.classList.toggle('open', !isOpen);
          panel.hidden = isOpen;
        });
      });
    }

    syncHeroPrice() {
      const source = this.querySelector('.pdp__price');
      const target = this.querySelector('#cinematic-hero-price');
      if (!source || !target || !('MutationObserver' in window)) return;
      const update = () => { target.innerHTML = source.innerHTML; };
      this.priceObserver = new MutationObserver(update);
      this.priceObserver.observe(source, { childList: true, subtree: true, characterData: true });
    }

    updateUI() {
      const variant = this.currentVariant;
      if (!variant) return;
      if (this.variantInput) this.variantInput.value = variant.id;
      this.priceEls.forEach((el) => { el.innerHTML = this.formatPrice(variant); });
      this.addToCartButtons.forEach((button) => {
        button.dataset.variantId = variant.id;
        button.disabled = !variant.available;
        button.textContent = variant.available ? 'Add to Cart' : 'Sold Out';
      });
      if (variant.featured_image_src && this.heroImage) {
        this.setHeroImageSource(variant.featured_image_src);
      }
      this.refreshHeroRenderRail();
      const stickyPrice = this.querySelector('.sticky-atc__price');
      if (stickyPrice) stickyPrice.innerHTML = this.formatPrice(variant);
      if (window.history?.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }
  }

  window.customElements.define('cinematic-product', CinematicProduct);
})();
